import firebase from "firebase/app";
import chunk from "lodash/chunk";
import firestore from "src/apiService/firestore";
import FirebaseBatcher from "src/utils/FirebaseBatcher";
import parallelize from "src/utils/parallelize";

import { Inventory } from "../../interfaces";
import { getUserId } from "src/config/storage";
import { stampNormalizedMatchFields } from "src/utils/normalizeMatchKey";

import { MaximumBatchSize } from "src/config/firestore";

export async function updateInventory(payload: Inventory) {
  if (!payload?.id) return;
  const db = firestore();
  const user = await getUserId();
  payload = stampNormalizedMatchFields({
    ...payload,
    user,
  });

  for (const key of Object.keys(payload)) {
    if (payload[key] === undefined) delete payload[key];
  }

  await db.collection("Inventory").doc(payload.id).update(payload);
}

export async function deleteInventoryApi(id: string) {
  if (!id) return;

  const db = firestore();
  await db.runTransaction(async (t) => {
    const doc = db.collection("Inventory").doc(id);
    const item = await doc.get();
    const data = item.data();
    const transactionInventoryId = data?.transaction_inventory_id;
    if (transactionInventoryId) {
      const transactionDoc = db
        .collection("Transactions")
        .doc(transactionInventoryId);
      try {
        const transaction = await t.get(transactionDoc);
        if (transaction.exists) {
          const transactionData = transaction.data();
          await t.update(transactionDoc, {
            items: (transactionData?.items || []).filter(
              ({ type, id: itemId }) => type !== "inventory" || itemId !== id
            ),
          });
        }
      } catch (e) {
        console.warn(
          "[inventories] deleteInventoryApi: failed to unlink from linked transaction; continuing delete",
          { inventoryId: id, transactionInventoryId, error: e }
        );
      }
    }

    await t.delete(doc);
  });
}

export async function deleteBulkInventoryApi(ids: string[]) {
  if (!ids.length) return;
  const db = firestore();

  // Batch-fetch all inventory docs to categorise as simple vs complex
  const simpleIds: string[] = [];
  const complexItems: { id: string; parentId: string }[] = [];

  for (const batchIds of chunk(ids, MaximumBatchSize)) {
    const snapshots = await Promise.all(
      batchIds.map((id) => db.collection("Inventory").doc(id).get())
    );
    for (const snap of snapshots) {
      if (!snap.exists) continue;
      const parentId = snap.data()?.transaction_inventory_id;
      if (parentId) {
        complexItems.push({ id: snap.id, parentId });
      } else {
        simpleIds.push(snap.id);
      }
    }
  }

  // Batch-delete simple items (no parent Transaction to update)
  const batcher = new FirebaseBatcher();
  for (const id of simpleIds) {
    await batcher.delete(db.collection("Inventory").doc(id));
  }
  await batcher.commit();

  // Group complex items by parent Transaction so we update each parent once
  const byParent = new Map<string, string[]>();
  for (const { id, parentId } of complexItems) {
    const list = byParent.get(parentId) || [];
    list.push(id);
    byParent.set(parentId, list);
  }

  const parentEntries = Array.from(byParent.entries());
  await parallelize(
    parentEntries,
    ([parentId, childIds]) =>
      db.runTransaction(async (t) => {
        const parentRef = db.collection("Transactions").doc(parentId);
        const parentSnap = await t.get(parentRef);
        if (parentSnap.exists) {
          const childSet = new Set(childIds);
          t.update(parentRef, {
            items: (parentSnap.data()?.items || []).filter(
              ({ type, id: itemId }: { type: string; id: string }) =>
                type !== "inventory" || !childSet.has(itemId)
            ),
          });
        }
        for (const id of childIds) {
          t.delete(db.collection("Inventory").doc(id));
        }
      }),
    10
  );
}

// TODO: type
export async function addInventory({
  quantity,
  ...payload
}: any): Promise<string> {
  const user = await getUserId();
  const db = firestore();
  quantity = parseInt(quantity, 10) || 1;

  for (const k of Object.keys(payload)) {
    if (payload[k] === undefined) delete payload[k];
  }

  const doc = db.collection("Inventory").doc();
  await doc.set(
    stampNormalizedMatchFields({
      ...payload,
      quantity,
      return_id: payload.return_id || null,
      user,
      item_option: null,
      id: doc.id,
    })
  );

  return doc.id;
}

export async function updateInventoryItems(
  items: (Pick<Inventory, "id"> & Partial<Inventory>)[]
) {
  if (!items.length) return;
  const db = firestore();
  for (const c of chunk(items, MaximumBatchSize)) {
    const batch = db.batch();
    for (const item of c) {
      const doc = db.collection("Inventory").doc(item.id);
      batch.update(doc, stampNormalizedMatchFields(item));
    }
    await batch.commit();
  }
}

export async function uploadInventoryFile(rows, metadata = {}) {
  const f = firebase.functions().httpsCallable("upload-uploadInventory");

  return (await f({ rows, metadata })).data;
}

export async function bulkEditInventory(rows, metadata = {}) {
  const f = firebase.functions().httpsCallable("upload-bulkEditInventory");
  const ret = (await f({ rows, metadata })).data;
  return ret;
}
